// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { partNumbersStyle } from "@conklin/App/ComponentExtensions/ProductPartNumbersExtensions";
import { getNotifyStyle } from "@conklin/App/Components/Notify";
import { TooltipPresentationProps } from "@conklin/MobiusOverrides/Tooltip";
import { ProductPriceStyles } from "@conklin/Overrides/Components/ProductPrice";
import IconClose from "@conklin/XDL/Icons/IconClose";
import IconNotificationTooltip from "@conklin/XDL/Icons/IconNotificationTooltip";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { color, createLinkIconStyles, sharedProductDescriptionStyles, xScreen } from "@conklin/XDL/Utilities";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import List from "@insite/mobius/Icons/List";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

const priceStyle: TypographyPresentationProps = {
    size: 12,
    weight: fontConfig.weight.bold,
    lineHeight: "15px",
    css: css`
        &&& {
            line-height: 15px;
            ${xScreen("lte", "md")} {
                font-size: 14px;
                line-height: 17px;
            }
        }
    `,
};

const totalPriceStyle: TypographyPresentationProps = {
    size: 16,
    weight: fontConfig.weight.bold,
    lineHeight: "19px",
    css: css`
        text-align: start;
        display: flex;
        justify-content: flex-end;

        ${xScreen("lte", "md")} {
            justify-content: flex-start;
            font-size: 14px;
            line-height: 17px;
        }
    `,
};

export interface CartLineSharedStyles {
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    addToListLink?: LinkPresentationProps;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    quantityTextField?: TextFieldPresentationProps;
    qtyLabel?: TypographyProps;
    productPrice?: ProductPriceStyles;
    extendedUnitNetPriceText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeValueText?: TypographyPresentationProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionsContainer?: InjectableCss;
    promotionNameLabel?: TypographyPresentationProps;
    promotionNameText?: InjectableCss;
    removeCartLineIcon?: IconPresentationProps;
    cartLineNotes?: CartLineNotesStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    errorMessage?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    configurationOptionText?: TypographyProps;
    configurationOptionLabel?: TypographyProps;
    unitOfMeasure?: TypographyProps;
    quoteMessage?: {
        quoteMessageWrapper?: InjectableCss;
        quoteTotal?: TypographyPresentationProps;
        quoteText?: TypographyPresentationProps;
        quoteTooltip?: TooltipPresentationProps;
    };
    invalidPriceHeadingAndText?: SmallHeadingAndTextStyles;
}

const cartLineSharedStyles: CartLineSharedStyles = {
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 91px;
                height: 91px;
                background-color: ${color("common.white")};
                justify-content: center;
                &&& > img {
                    max-width: 100%;
                    max-height: 100%;
                    height: auto;
                    object-fit: contain;
                    font-size: 14px;
                }
            `,
        },
    },
    productDescription: {
        ...sharedProductDescriptionStyles(2, 2),
    },
    productPartNumbers: {
        container: {
            gap: 0,
            css: css`
                @media print {
                    display: block;
                    width: 100%;
                }
            `,
        },
        erpNumberGridItem: {
            css: css`
                flex-basis: auto;
                word-break: keep-all;
            `,
        },
    },
    addToListLink: {
        ...createLinkIconStyles(
            List,
            "small",
            "left",
            false,
            {
                css: css`
                    white-space: nowrap;

                    ${IconWrapper} {
                        width: 14px;
                        height: 14px;
                    }

                    ${({ displayLineNote }: any) =>
                        displayLineNote &&
                        css`
                            position: absolute;
                            left: 130px;
                            ${xScreen("lte", "md")} {
                                right: 0;
                                left: unset;
                            }
                        `}
                `,
            },
            {
                lineHeight: "15px",
                weight: fontConfig.weight.bold,
            },
        ),
    },
    productPrice: {
        priceLabelText: {
            size: 12,
            weight: fontConfig.weight.regular,
            css: css`
                text-transform: capitalize;
                padding: 0;
                margin-bottom: 4px;
                display: none;
            `,
        },
        priceWrapper: {
            css: css`
                line-height: 17px;
                padding-top: 2px;
            `,
        },
        price: {
            priceText: priceStyle,
            errorText: priceStyle,
            invalidPriceText: priceStyle,
            unitOfMeasure: {
                unitOfMeasureText: {
                    size: 10,
                    lineHeight: "14px",
                    weight: fontConfig.weight.regular,
                    css: css`
                        white-space: nowrap;
                        &&& {
                            white-space: nowrap;
                            ${xScreen("lte", "md")} {
                                font-size: 12px;
                                line-height: 15px;
                            }
                        }
                    `,
                },
            },
        },
    },
    extendedUnitNetPriceText: {
        extendedStyles: {
            heading: {
                size: 12,
                lineHeight: "15px",
                weight: "normal",
                css: css`
                    line-height: 15px;
                    text-transform: capitalize;
                    padding: 0;
                    margin-bottom: 4px;
                    display: none;
                    ${xScreen("lte", "md")} {
                        margin-bottom: 6px;
                        display: block;
                    }
                    @media print {
                        &&& {
                            font-size: 14px;
                            line-height: 16px;
                        }
                    }
                `,
            },
            text: { ...totalPriceStyle },
        },
    },
    invalidPriceHeadingAndText: {
        extendedStyles: {
            heading: {
                size: 12,
                lineHeight: "15px",
                weight: "normal",
                css: css`
                    line-height: 15px;
                    text-transform: capitalize;
                    padding: 0;
                    margin-bottom: 10px;
                    display: none;
                    ${xScreen("lte", "md")} {
                        margin-bottom: 6px;
                        display: block;
                    }
                    @media print {
                        &&& {
                            font-size: 14px;
                            line-height: 16px;
                        }
                    }
                `,
            },
            text: {
                ...totalPriceStyle,
            },
        },
    },
    costCodeGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    costCodeLabelText: {
        ...partNumbersStyle.label,
    },
    costCodeValueText: {
        ...partNumbersStyle.value,
    },
    configurationOptionText: {
        ...partNumbersStyle.value,
    },
    configurationOptionLabel: {
        ...partNumbersStyle.label,
    },
    promotionsContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    promotionNameLabel: {
        ...partNumbersStyle.label,
    },
    promotionNameText: {
        ...partNumbersStyle.value,
    },
    qtyLabel: {
        as: "label",
        size: 12,
        weight: "normal",
        lineHeight: "17px",
        css: css`
            margin-bottom: 4px;
            display: none;
            ${xScreen("lte", "md")} {
                display: block;
            }
            @media print {
                &&& {
                    font-size: 14px;
                    line-height: 16px;
                }
            }
        `,
    },
    removeCartLineIcon: {
        src: IconClose,
        size: 12,
        color: "text.ui6",
        css: css`
            &:hover {
                color: ${color("common.ui10")};
            }
        `,
    },
    cartLineNotes: {
        wrapper: {
            css: css`
                ${xScreen("lte", "md")} {
                    width: 100%;
                }
            `,
        },
        editableNotesTextArea: {
            css: css`
                &&&& {
                    overflow: auto;
                }
            `,
        },
    },
    cartLineErrorMessageGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            ${xScreen("lte", "md")} {
                margin-top: 10px;
            }
        `,
    },
    errorMessage: {
        ...getNotifyStyle("danger"),
    },
    productAvailability: {
        errorWrapper: {
            css: css`
                margin-bottom: 12px;
            `,
        },
        messageGridItem: {
            css: css`
                &:not(:empty) {
                    margin-bottom: 12px;
                }
            `,
        },
    },
    unitOfMeasure: {
        size: 12,
        weight: "normal",
        css: css`
            line-height: 13px;
            text-transform: capitalize;
        `,
    },
    quoteMessage: {
        quoteMessageWrapper: {
            css: css`
                display: flex;
                justify-content: flex-end;
                flex-wrap: wrap;
                row-gap: 6px;
                ${xScreen("lte", "md")} {
                    justify-content: flex-start;
                }
            `,
        },
        quoteTotal: {
            size: fontConfig.fontSize.small,
            lineHeight: fontConfig.lineHeight.small,
            css: css`
                flex: 0 0 100%;
                display: none;
                ${xScreen("lte", "md")} {
                    display: block;
                }
            `,
        },
        quoteText: totalPriceStyle,
        quoteTooltip: {
            iconProps: {
                src: IconNotificationTooltip,
                color: "common.ui9",
                size: 14,
            },
            extendedStyles: {
                triggerWrapper: {
                    css: css`
                        margin-left: 6px;
                        margin-right: 0;
                        margin-top: 1px;
                    `,
                },
            },
        },
    },
};

export default cartLineSharedStyles;
